import { useState } from 'react';
import type { ConsoleRole, Member, MemberScope } from '@glyph/protocol-ts';
import { fmtAgo, useDevices, useEvents, useMe, useMembers } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  CellMono,
  Checkbox,
  DataTable,
  Dialog,
  IconButton,
  ListRow,
  Note,
  PageHeader,
  Section,
  Select,
  StatusPill,
  TextInput,
  useToast,
} from '@glyph/ui';

type Grantable = Exclude<ConsoleRole, 'platform'>;
const ROLES: { value: Grantable; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'event_admin', label: 'Event admin' },
  { value: 'operator', label: 'Operator' },
  { value: 'room_operator', label: 'Room operator' },
  { value: 'viewer', label: 'Viewer' },
];
const ROLE_HELP: [string, string][] = [
  ['Owner', 'everything · org settings · API tokens · alert rules and routing · grants admin and owner'],
  ['Admin', 'every unit · network and system settings · accounts below admin · groups · presets'],
  ['Event admin', 'its own groups only · their units, presets and its own team (event admins, operators, room operators, viewers)'],
  ['Operator', 'live control · captions · speech · recordings · listen · ack · on all units or the ones assigned'],
  ['Room operator', 'its assigned rooms only · moves the captions (top / middle / bottom) · nothing else'],
  ['Viewer', 'preview, listen and download only · no changes · all units or the ones assigned'],
];
/** Phones and tablets capitalise the first letter and autocorrect plain text fields: never on a username or a
 * password someone will have to type back exactly (2026-10-24: an account's password was saved as typed by the
 * keyboard, not as the admin meant it). */
const NO_AUTOFIX = { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } as const;
/** Roles that only work inside assigned units / groups. */
const SCOPED = new Set<ConsoleRole>(['room_operator', 'event_admin']);
const RANK: Record<string, number> = { viewer: 0, room_operator: 1, operator: 2, event_admin: 3, admin: 4, owner: 5, platform: 6 };
/** What the signed-in account may hand out (the cloud enforces the same rule). */
function grantable(actor: ConsoleRole | undefined): Grantable[] {
  if (actor === 'platform' || actor === 'owner') return ROLES.map((r) => r.value);
  if (actor === 'admin') return ['event_admin', 'operator', 'room_operator', 'viewer'];
  if (actor === 'event_admin') return ['event_admin', 'operator', 'room_operator', 'viewer'];
  return [];
}

/** Pick the groups and units an account may use. Lists only what the signed-in account itself can see. */
function AccessPicker({ value, onChange }: { value: MemberScope; onChange: (v: MemberScope) => void }) {
  const groups = useEvents();
  const devices = useDevices();
  const units = (devices.data ?? []).filter((d) => d.status !== 'pending');
  const toggle = (key: 'devices' | 'events', id: string) =>
    onChange({ ...value, [key]: value[key].includes(id) ? value[key].filter((x) => x !== id) : [...value[key], id] });
  return (
    <div className="flex flex-col gap-3" data-testid="access-picker">
      <div className="flex flex-col gap-1">
        <span className="micro text-fg3">Groups · every unit in the group, now and later</span>
        {(groups.data ?? []).length === 0 && <span className="font-mono text-xs text-fg4">No groups</span>}
        {(groups.data ?? []).map((g) => (
          <label key={g.id} className="flex items-center gap-2 py-1 text-base">
            <Checkbox checked={value.events.includes(g.id)} onChange={() => toggle('events', g.id)} label={`Group ${g.name}`} />
            <span className="min-w-0 flex-1 truncate">{g.name}</span>
          </label>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <span className="micro text-fg3">Units</span>
        {units.map((d) => (
          <label key={d.id} className="flex items-center gap-2 py-1 text-base">
            <Checkbox checked={value.devices.includes(d.id)} onChange={() => toggle('devices', d.id)} label={`Unit ${d.name}`} />
            <span className="min-w-0 flex-1 truncate">{d.name}</span>
            <span className="font-mono text-xs text-fg4">{d.assignment?.event_name ?? ''}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Users & access: invite (link shown once) + role, table with inline role select, 2FA, last seen; Roles card. */
export function UsersScreen() {
  const m = useMembers();
  const me = useMe();
  const groups = useEvents();
  const devices = useDevices();
  const toast = useToast();
  const actor = me.data?.role;
  const canGrant = grantable(actor);
  const roleOptions = ROLES.filter((r) => canGrant.includes(r.value));
  // An event admin's team always lives inside its groups; everyone else may also give "the whole organisation".
  const mustScope = (r: ConsoleRole) => SCOPED.has(r) || actor === 'event_admin';
  const manageable = (u: Member) => u.id !== me.data?.id && (actor === 'platform' || canGrant.includes(u.role as Grantable));
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Grantable>('operator');
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [creating, setCreating] = useState<{ username: string; name: string; password: string; role: Grantable; scope: MemberScope } | null>(null);
  const [access, setAccess] = useState<{ member: Member; scope: MemberScope; role?: Grantable } | null>(null);
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);
  const [editing, setEditing] = useState<{ member: Member; name: string; username: string; password: string } | null>(null);
  const list = m.data ?? [];
  const genPassword = () => {
    const words = ['amber', 'cedar', 'delta', 'ember', 'flint', 'glyph', 'harbor', 'indigo', 'juniper', 'kestrel', 'lumen', 'maple', 'nova', 'onyx', 'pixel', 'quartz', 'river', 'sonic', 'tundra', 'vector'];
    const pick = () => words[Math.floor(Math.random() * words.length)];
    return `${pick()}-${pick()}-${Math.floor(100 + Math.random() * 900)}`;
  };
  const createAccount = async () => {
    if (!creating) return;
    try {
      const scoped = creating.scope.devices.length || creating.scope.events.length;
      const u = await m.create({ username: creating.username.trim(), password: creating.password, name: creating.name.trim(), role: creating.role, scope: scoped ? creating.scope : null });
      setCreds({ username: u.username ?? creating.username, password: creating.password });
      setCreating(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const { member } = editing;
    const body: { id: string; name?: string; username?: string; password?: string } = { id: member.id };
    if (editing.name.trim() && editing.name.trim() !== member.name) body.name = editing.name.trim();
    if (editing.username.trim() && editing.username.trim().toLowerCase() !== (member.username ?? '')) body.username = editing.username.trim();
    if (editing.password) body.password = editing.password;
    if (Object.keys(body).length === 1) {
      setEditing(null);
      return;
    }
    try {
      await m.patch(body);
      if (body.password) setCreds({ username: (body.username ?? member.username ?? member.email).toLowerCase(), password: body.password });
      else toast.push(`Saved ${body.name ?? member.name}`, 'ok');
      setEditing(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const openEdit = (u: Member) => setEditing({ member: u, name: u.name, username: u.username ?? '', password: '' });
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.push('Copied', 'ok');
    } catch {
      toast.push('Select the text and copy it', 'warn');
    }
  };
  const changeRole = async (u: Member, r: Grantable) => {
    // a room operator / event admin only works inside units or groups: pick them first
    if (SCOPED.has(r) && !u.scope) {
      setAccess({ member: u, scope: { devices: [], events: [] }, role: r });
      return;
    }
    try {
      await m.setRole({ id: u.id, role: r });
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const saveAccess = async () => {
    if (!access) return;
    try {
      await m.setScope({ id: access.member.id, scope: access.scope });
      if (access.role && access.role !== access.member.role) await m.setRole({ id: access.member.id, role: access.role });
      toast.push(`Access saved for ${access.member.name}`, 'ok');
      setAccess(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const groupName = (id: string) => groups.data?.find((g) => g.id === id)?.name ?? 'group';
  const unitName = (id: string) => devices.data?.find((d) => d.id === id)?.name ?? 'unit';
  const accessTxt = (u: Member) => {
    if (u.status === 'invited') return SCOPED.has(u.role) ? 'Set after sign-up' : 'Whole org';
    if (!u.scope) return RANK[u.role]! >= RANK.admin! ? 'Whole org' : SCOPED.has(u.role) ? 'Nothing yet' : 'Whole org';
    const parts = [...u.scope.events.map(groupName), ...u.scope.devices.map(unitName)];
    return parts.join(' · ') || 'Nothing yet';
  };
  const accessBtn = (u: Member) =>
    u.status === 'active' && manageable(u) && RANK[u.role]! < RANK.admin! ? (
      <Button size="sm" onClick={() => setAccess({ member: u, scope: u.scope ? { devices: [...u.scope.devices], events: [...u.scope.events] } : { devices: [], events: [] } })}>
        Access
      </Button>
    ) : null;
  const roleCell = (u: Member, block = false) =>
    manageable(u) ? (
      <Select
        size="sm"
        block={block}
        value={u.role as Grantable}
        onChange={(r) => void changeRole(u, r)}
        options={roleOptions}
        aria-label={`Role for ${u.name}`}
      />
    ) : (
      <CellMono>{ROLES.find((r) => r.value === u.role)?.label ?? u.role}</CellMono>
    );
  const newAccountBtn = canGrant.length > 0 && (
    <Button size="responsive" onClick={() => setCreating({ username: '', name: '', password: genPassword(), role: canGrant.includes('operator') ? 'operator' : canGrant[0]!, scope: { devices: [], events: [] } })}>
      New account
    </Button>
  );
  // Shown once: the only time the password is readable.
  const credsCard = creds && (
    <Card mode="padded" gap="sm">
      <div className="flex items-center justify-between gap-3">
        <span className="text-md font-semibold">Sign-in details for {creds.username}</span>
        <IconButton framed={false} label="Dismiss" className="text-xl text-fg5" onClick={() => setCreds(null)}>
          ×
        </IconButton>
      </div>
      <TextInput variant="mono" readOnly value={`${location.origin}  ·  ${creds.username}  ·  ${creds.password}`} onFocus={(e) => e.currentTarget.select()} block />
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" size="responsive" onClick={() => void copy(`Sign in at ${location.origin}\nUsername: ${creds.username}\nPassword: ${creds.password}`)}>
          Copy details
        </Button>
        <span className="font-mono text-xs text-fg4">THE PASSWORD IS NOT SHOWN AGAIN</span>
      </div>
    </Card>
  );
  const invite = async () => {
    if (!email.includes('@')) return;
    const r = (await m.invite({ email, role })) as Member & { invite_url?: string | null };
    setEmail('');
    if (r.invite_url) {
      setLink({ email, url: r.invite_url });
      toast.push(`Invite created for ${email}`, 'ok');
    } else toast.push(`Invite sent to ${email}`, 'ok');
  };
  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.push('Invite link copied', 'ok');
    } catch {
      toast.push('Select the link and copy it', 'warn');
    }
  };
  // Shown once after an invite: the link is the only copy when no email provider is configured.
  const linkCard = link && (
    <Card mode="padded" gap="sm">
      <div className="flex items-center justify-between gap-3">
        <span className="text-md font-semibold">Invite link for {link.email}</span>
        <IconButton framed={false} label="Dismiss" className="text-xl text-fg5" onClick={() => setLink(null)}>
          ×
        </IconButton>
      </div>
      <TextInput variant="mono" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} block />
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" size="responsive" onClick={() => void copyLink()}>
          Copy link
        </Button>
        <span className="font-mono text-xs text-fg4">VALID 7 DAYS · SEND IT TO THEM YOURSELF UNLESS EMAIL IS SET UP</span>
      </div>
    </Card>
  );
  const seen = (u: Member) =>
    u.status === 'invited' ? 'Invited' : u.last_seen ? fmtAgo(u.last_seen).replace('just now', 'Now') : '—';
  // invites carry no units or groups: an event admin builds its team with accounts instead
  const inviteRow = actor !== 'event_admin' && canGrant.length > 0 && (
    <div className="flex flex-wrap items-center gap-2">
      <TextInput
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void invite()}
        placeholder="name@company.com"
        className="w-full md:w-[220px]"
        type="email"
      />
      <Select value={role} onChange={setRole} options={roleOptions} aria-label="Role" />
      <Button
        variant="primary"
        size="responsive"
        onClick={() => void invite()}
        disabled={!email.includes('@')}
      >
        Invite
      </Button>
    </div>
  );
  const roles = (
    <Card>
      <CardHeader title="Roles" />
      {ROLE_HELP.map(([r, h]) => (
        <div key={r} className="flex flex-col gap-1 border-t border-line px-4 py-[11px]">
          <span className="text-base font-medium">{r}</span>
          <span className="font-mono text-xs leading-[1.5] text-fg4">{h}</span>
        </div>
      ))}
    </Card>
  );
  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Users & access"
        meta={`${list.length} ACCOUNTS`}
        actions={
          <>
            {inviteRow}
            {newAccountBtn}
          </>
        }
      />
      {linkCard}
      {credsCard}
      <div className="flex flex-col gap-4 md:hidden">
        {(inviteRow || newAccountBtn) && (
          <Card mode="padded" gap="md">
            <span className="text-md font-semibold">Add someone</span>
            {inviteRow}
            {newAccountBtn}
          </Card>
        )}
        <Card>
          <CardHeader title="Accounts" meta={`${list.length}`} />
          {list.map((u) => (
            <ListRow
              key={u.id}
              title={u.name}
              sub={`${u.username ?? u.email} · ${accessTxt(u)} · seen ${seen(u)}`}
              trailing={
                <div className="flex items-center gap-2">
                  {u.status === 'active' && manageable(u) && (
                    <Button size="sm" onClick={() => openEdit(u)} aria-label={`Edit ${u.name}`}>
                      Edit
                    </Button>
                  )}
                  {accessBtn(u)}
                  {roleCell(u)}
                </div>
              }
            />
          ))}
        </Card>
        {roles}
      </div>
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <Card className="min-w-0" style={{ flex: '1 1 560px' }}>
          <DataTable
            rows={list}
            rowKey={(u) => u.id}
            dense
            minWidth={760}
            columns={[
              {
                key: 'name',
                header: 'Name',
                width: '1.2fr',
                cell: (u) => <span className="text-md font-medium leading-[1.2]">{u.name}</span>,
              },
              {
                key: 'email',
                header: 'Sign-in',
                width: '1.2fr',
                cell: (u) => <CellMono className="block truncate">{u.username ? `${u.username}${u.email ? ` · ${u.email}` : ''}` : u.email}</CellMono>,
              },
              {
                key: 'role',
                header: 'Role',
                width: '1.25fr',
                cell: (u) => roleCell(u, true),
              },
              {
                key: 'access',
                header: 'Access',
                width: '1.8fr',
                cell: (u) => (
                  <div className="flex min-w-0 items-center gap-2">
                    <CellMono className="min-w-0 flex-1 truncate">{accessTxt(u)}</CellMono>
                    {accessBtn(u)}
                  </div>
                ),
              },
              {
                key: 'mfa',
                header: '2FA',
                width: '.5fr',
                cell: (u) => <StatusPill tone={u.mfa ? 'ok' : 'warn'}>{u.mfa ? 'ON' : 'OFF'}</StatusPill>,
              },
              {
                key: 'seen',
                header: 'Last seen',
                width: '.8fr',
                cell: (u) => <CellMono>{seen(u)}</CellMono>,
              },
              {
                key: 'pw',
                header: '',
                width: '72px',
                cell: (u) =>
                  u.status === 'active' && manageable(u) ? (
                    <Button size="sm" onClick={() => openEdit(u)} aria-label={`Edit ${u.name}`}>
                      Edit
                    </Button>
                  ) : null,
              },
              {
                key: 'rm',
                header: '',
                width: '24px',
                cell: (u) =>
                  manageable(u) ? (
                    <IconButton
                      framed={false}
                      danger
                      label={`Remove ${u.name}`}
                      className="text-xl text-fg5"
                      onClick={() => void m.remove(u.id).catch((e: Error) => toast.push(e.message, 'warn'))}
                    >
                      ×
                    </IconButton>
                  ) : null,
              },
            ]}
          />
        </Card>
        <div className="flex min-w-[280px] flex-col gap-5" style={{ flex: '0 1 320px' }}>
          {roles}
        </div>
      </div>
      <Dialog
        open={!!creating}
        onClose={() => setCreating(null)}
        title="New account"
        confirm={{
          label: 'Create',
          onClick: createAccount,
          disabled:
            !creating ||
            !/^[a-z0-9][a-z0-9._-]{2,31}$/i.test(creating.username.trim()) ||
            creating.password.length < 8 ||
            (mustScope(creating.role) && !creating.scope.devices.length && !creating.scope.events.length),
          busy: m.busy,
        }}
      >
        {creating && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Username</span>
              <TextInput variant="mono" {...NO_AUTOFIX} autoComplete="off" value={creating.username} onChange={(e) => setCreating({ ...creating, username: e.target.value })} block autoFocus />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Name</span>
              <TextInput value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} block />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Password</span>
              <TextInput variant="mono" {...NO_AUTOFIX} autoComplete="new-password" value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} block />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Role</span>
              <Select value={creating.role} onChange={(role) => setCreating({ ...creating, role })} options={roleOptions} block />
            </label>
            {RANK[creating.role]! < RANK.admin! && (
              <>
                <span className="font-mono text-xs text-fg4">
                  {mustScope(creating.role) ? 'PICK WHAT THIS ACCOUNT MAY USE' : 'LEAVE EMPTY FOR EVERY UNIT IN THE ORGANISATION'}
                </span>
                <AccessPicker value={creating.scope} onChange={(scope) => setCreating({ ...creating, scope })} />
              </>
            )}
          </div>
        )}
      </Dialog>
      <Dialog
        open={!!access}
        onClose={() => setAccess(null)}
        title={access ? `Access for ${access.member.name}` : ''}
        meta={access?.role ? `BECOMES ${(ROLES.find((r) => r.value === access.role)?.label ?? access.role).toUpperCase()}` : undefined}
        confirm={{
          label: 'Save access',
          onClick: saveAccess,
          disabled: !access || (mustScope(access.role ?? access.member.role) && !access.scope.devices.length && !access.scope.events.length),
          busy: m.busy,
        }}
      >
        {access && (
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs text-fg4">
              {mustScope(access.role ?? access.member.role) ? 'THIS ROLE ONLY WORKS INSIDE THE UNITS AND GROUPS PICKED HERE' : 'NOTHING PICKED = EVERY UNIT IN THE ORGANISATION'}
            </span>
            <AccessPicker value={access.scope} onChange={(scope) => setAccess({ ...access, scope })} />
          </div>
        )}
      </Dialog>
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Edit ${editing.member.name}` : ''}
        meta={editing ? `${(ROLES.find((r) => r.value === editing.member.role)?.label ?? editing.member.role).toUpperCase()} · ROLE AND ACCESS ARE IN THE TABLE` : undefined}
        confirm={{
          label: 'Save',
          onClick: saveEdit,
          disabled: !editing || (!!editing.password && editing.password.length < 8) || (!!editing.username && !/^[a-z0-9][a-z0-9._-]{2,31}$/i.test(editing.username.trim())),
          busy: m.busy,
        }}
      >
        {editing && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Name</span>
              <TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} block aria-label="Name" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Username · signs in with this</span>
              <TextInput variant="mono" {...NO_AUTOFIX} autoComplete="off" value={editing.username} onChange={(e) => setEditing({ ...editing, username: e.target.value })} block aria-label="Username" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">New password · leave empty to keep the current one</span>
              <div className="flex gap-2">
                <TextInput
                  variant="mono"
                  {...NO_AUTOFIX}
                  autoComplete="new-password"
                  value={editing.password}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  className="min-w-0 flex-1"
                  aria-label="New password"
                />
                <Button size="responsive" onClick={() => setEditing({ ...editing, password: genPassword() })}>
                  Generate
                </Button>
              </div>
            </label>
            {editing.password && <Note>They sign in with exactly what is shown here; the next screen shows it once more to copy.</Note>}
          </div>
        )}
      </Dialog>
    </Section>
  );
}
